import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth, isAdmin, isPartner, isAgent, isAreaManager } from '@/contexts/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Plus, LifeBuoy, Send, Loader2, CheckCircle2, RotateCcw, MessageSquare, Paperclip, X, FileText, Image as ImageIcon, Search, UserCheck } from 'lucide-react';

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'application/pdf'];

// Selettore file riutilizzabile per allegati ticket (max 3, 5MB, immagini/PDF)
const AttachmentPicker = ({ attachments, setAttachments, idPrefix }) => {
  const inputRef = React.useRef(null);
  const addFiles = async (files) => {
    const list = [...attachments];
    for (const file of files) {
      if (list.length >= 3) { toast.error('Massimo 3 allegati per messaggio'); break; }
      if (!ALLOWED_TYPES.includes(file.type)) { toast.error(`Formato non supportato: ${file.name} (usa immagini o PDF)`); continue; }
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} supera i 5MB`); continue; }
      try {
        const dataUrl = await readFileAsDataUrl(file);
        list.push({ filename: file.name, content_type: file.type, data_url: dataUrl });
      } catch { toast.error(`Impossibile leggere ${file.name}`); }
    }
    setAttachments(list);
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 items-center">
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} data-testid={`${idPrefix}-attach-button`}>
          <Paperclip className="size-3.5 mr-1" />Allega file
        </Button>
        <span className="text-[11px] text-muted-foreground">Immagini o PDF · max 3 file · 5MB ciascuno</span>
        <input
          ref={inputRef} type="file" multiple className="hidden"
          accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
          data-testid={`${idPrefix}-attach-input`}
          onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
        />
      </div>
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {attachments.map((a, i) => (
            <div key={i} className="flex items-center gap-1.5 rounded-md border bg-secondary/40 pl-2 pr-1 py-1 text-xs max-w-[220px]" data-testid={`${idPrefix}-attachment-chip`}>
              {a.content_type === 'application/pdf' ? <FileText className="size-3.5 shrink-0 text-primary" /> : <ImageIcon className="size-3.5 shrink-0 text-primary" />}
              <span className="truncate">{a.filename}</span>
              <Button type="button" variant="ghost" size="icon" className="size-5" onClick={() => setAttachments(attachments.filter((_, j) => j !== i))}>
                <X className="size-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Render allegati di un messaggio nel thread
const MessageAttachments = ({ attachments }) => {
  if (!attachments?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {attachments.map((a, i) => a.content_type === 'application/pdf' ? (
        <a
          key={i} href={a.data_url} download={a.filename || 'allegato.pdf'}
          className="flex items-center gap-1.5 rounded-md border bg-background px-2 py-1.5 text-xs hover:bg-secondary transition-colors"
          data-testid="ticket-attachment-pdf"
        >
          <FileText className="size-3.5 text-primary" />
          <span className="truncate max-w-[180px]">{a.filename || 'Documento PDF'}</span>
        </a>
      ) : (
        <a key={i} href={a.data_url} download={a.filename || 'immagine'} title={`Scarica ${a.filename || 'immagine'}`} data-testid="ticket-attachment-image">
          <img src={a.data_url} alt={a.filename || ''} className="h-24 max-w-[180px] rounded-md border object-cover hover:opacity-90 transition-opacity" />
        </a>
      ))}
    </div>
  );
};

const CATEGORY_LABELS = { richiesta: 'Richiesta', informazioni: 'Informazioni', problema: 'Problema', altro: 'Altro' };
const PRIORITY_BADGE = { bassa: 'outline', media: 'secondary', alta: 'destructive' };
const STATUS_LABELS = { aperto: 'Aperto', in_lavorazione: 'In lavorazione', chiuso: 'Chiuso' };
const STATUS_BADGE = { aperto: 'default', in_lavorazione: 'secondary', chiuso: 'outline' };

const emptyForm = { subject: '', category: 'richiesta', priority: 'media', message: '' };

export default function Tickets() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const partner = isPartner(user);
  const agent = isAgent(user);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [operators, setOperators] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [targetCompanyId, setTargetCompanyId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [formAttachments, setFormAttachments] = useState([]);
  const [creating, setCreating] = useState(false);

  // Detail
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [sendingReply, setSendingReply] = useState(false);

  const notifySidebar = () => window.dispatchEvent(new Event('tickets-updated'));

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      setTickets(await api.listTickets(params));
    } catch (e) {
      toast.error('Errore caricamento ticket');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter, debouncedSearch]);

  // Debounce ricerca
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Operatori assegnabili (solo per chi gestisce ticket: admin o partner)
  useEffect(() => {
    if (!(admin || partner)) return;
    (async () => {
      try {
        const users = await api.listUsers();
        const eligible = users.filter(u => admin ? u.role === 'admin' : (u.role === 'partner' && u.company_id === user?.company_id));
        setOperators(eligible.filter(u => u.status !== 'disabled'));
      } catch { /* silenzioso */ }
    })();
    // eslint-disable-next-line
  }, [admin, partner]);

  // Aziende partner destinatarie (solo admin: per aprire ticket verso un partner)
  useEffect(() => {
    if (!admin) return;
    (async () => {
      try {
        const list = await api.listCompanies();
        setCompanies(list || []);
      } catch { /* silenzioso */ }
    })();
    // eslint-disable-next-line
  }, [admin]);

  // Utenti a cui posso assegnare un ticket (chain of command)
  useEffect(() => {
    if (!createOpen) return;
    (async () => {
      try {
        const r = await api.getTicketAssignableUsers();
        setAssignableUsers(r.users || []);
      } catch { setAssignableUsers([]); }
    })();
  }, [createOpen]);

  const ROLE_LABELS_LOCAL = { admin: 'Super Admin', partner: 'B.O.', area_manager: 'Area Manager', agent: 'Agente' };

  const targetLabel = 'Scegli il destinatario esplicito del ticket rispettando la catena di comando.';

  const description = admin
    ? 'Richieste ricevute dalle aziende partner e ticket aperti verso i partner.'
    : partner
      ? 'Richieste dei tuoi agenti, ticket verso Scintilla Hub e comunicazioni ricevute.'
      : 'Le tue richieste verso il responsabile della tua azienda.';

  const handleCreate = async () => {
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error('Oggetto e messaggio sono obbligatori');
      return;
    }
    if (!assigneeId) {
      toast.error('Seleziona un destinatario per il ticket');
      return;
    }
    setCreating(true);
    try {
      await api.createTicket({ ...form, attachments: formAttachments, assignee_id: assigneeId });
      toast.success('Ticket aperto');
      setCreateOpen(false);
      setForm(emptyForm);
      setTargetCompanyId('');
      setAssigneeId('');
      setFormAttachments([]);
      load();
      notifySidebar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore apertura ticket');
    } finally {
      setCreating(false);
    }
  };

  const openDetail = async (t) => {
    try {
      setSelected(await api.getTicket(t.id));
      setReply('');
      setReplyAttachments([]);
      notifySidebar();
    } catch { toast.error('Errore apertura ticket'); }
  };

  const handleReply = async () => {
    if (!reply.trim() && replyAttachments.length === 0) return;
    setSendingReply(true);
    try {
      await api.addTicketMessage(selected.id, reply, replyAttachments);
      setReply('');
      setReplyAttachments([]);
      setSelected(await api.getTicket(selected.id));
      load();
      notifySidebar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio messaggio');
    } finally {
      setSendingReply(false);
    }
  };

  const changeStatus = async (status) => {
    try {
      await api.setTicketStatus(selected.id, status);
      toast.success(status === 'chiuso' ? 'Ticket chiuso' : `Stato aggiornato: ${STATUS_LABELS[status]}`);
      setSelected(await api.getTicket(selected.id));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore aggiornamento stato');
    }
  };

  const handleAssign = async (userId) => {
    try {
      const res = await api.assignTicket(selected.id, userId === 'none' ? null : userId);
      toast.success(res.assigned_to ? `Assegnato a ${res.assigned_to_name}` : 'Assegnazione rimossa');
      setSelected(await api.getTicket(selected.id));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore assegnazione');
    }
  };

  // Chi può gestire lo stato del ticket selezionato
  const canHandle = useMemo(() => {
    if (!selected) return false;
    if (admin && (selected.target === 'admin' || selected.created_by_role === 'admin')) return true;
    if (partner && selected.target === 'partner') return true;
    return selected.created_by === user?.id;
  }, [selected, admin, partner, user]);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div>
      <PageHeader
        title="Ticket & Assistenza"
        description={description}
        actions={(
          <Button onClick={() => setCreateOpen(true)} data-testid="ticket-create-button">
            <Plus className="size-4 mr-2" />Apri ticket
          </Button>
        )}
      />

      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per oggetto, autore, azienda o contenuto..."
                className="pl-9"
                data-testid="ticket-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[190px]" data-testid="ticket-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="aperto">Aperti</SelectItem>
                <SelectItem value="in_lavorazione">In lavorazione</SelectItem>
                <SelectItem value="chiuso">Chiusi</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex-1" />
            <div className="text-xs text-muted-foreground shrink-0">{tickets.length} ticket</div>
          </div>
          {loading ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Caricamento...</div>
          ) : tickets.length === 0 ? (
            <div className="p-12 text-center">
              <div className="mx-auto size-12 rounded-full bg-secondary grid place-items-center mb-3"><LifeBuoy className="size-5 text-muted-foreground" /></div>
              <div className="text-sm font-medium">{debouncedSearch ? 'Nessun risultato' : 'Nessun ticket'}</div>
              <div className="text-xs text-muted-foreground mb-4">{debouncedSearch ? 'Prova con altri termini di ricerca.' : (admin ? 'Non ci sono richieste dalle aziende partner. Puoi aprire un ticket verso un partner.' : 'Apri un ticket per richieste, informazioni o segnalazioni.')}</div>
              {!debouncedSearch && <Button size="sm" onClick={() => setCreateOpen(true)}><Plus className="size-4 mr-2" />Apri ticket</Button>}
            </div>
          ) : (
            <Table data-testid="tickets-table">
              <TableHeader><TableRow>
                <TableHead>Oggetto</TableHead>
                {(admin || partner) && <TableHead>Da</TableHead>}
                <TableHead>Categoria</TableHead>
                <TableHead>Priorità</TableHead>
                <TableHead>Stato</TableHead>
                {(admin || partner) && <TableHead>Assegnato a</TableHead>}
                <TableHead className="text-right">Aggiornato</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {tickets.map(t => (
                  <TableRow key={t.id} className="cursor-pointer" onClick={() => openDetail(t)} data-testid={`ticket-row-${t.id}`}>
                    <TableCell>
                      <div className="font-medium flex items-center gap-2">
                        <MessageSquare className="size-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate max-w-[320px]">{t.subject}</span>
                      </div>
                      {partner && t.target === 'admin' && <div className="text-[11px] text-muted-foreground pl-5">Verso Scintilla Hub</div>}
                      {admin && t.created_by_role === 'admin' && <div className="text-[11px] text-muted-foreground pl-5">Verso {t.company_name}</div>}
                    </TableCell>
                    {(admin || partner) && (
                      <TableCell>
                        <div className="text-sm">{t.created_by_name}</div>
                        <div className="text-[11px] text-muted-foreground">{t.created_by_role === 'admin' ? 'Scintilla Hub' : admin ? t.company_name : (t.created_by_role === 'agent' ? 'Agente' : 'Responsabile')}</div>
                      </TableCell>
                    )}
                    <TableCell><Badge variant="secondary">{CATEGORY_LABELS[t.category] || t.category}</Badge></TableCell>
                    <TableCell><Badge variant={PRIORITY_BADGE[t.priority] || 'outline'} className="capitalize">{t.priority}</Badge></TableCell>
                    <TableCell><Badge variant={STATUS_BADGE[t.status] || 'outline'}>{STATUS_LABELS[t.status] || t.status}</Badge></TableCell>
                    {(admin || partner) && (
                      <TableCell>
                        {t.assigned_to_name ? (
                          <div className="flex items-center gap-1.5 text-xs"><UserCheck className="size-3.5 text-primary" /><span className="truncate max-w-[120px]">{t.assigned_to_name}</span></div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-right text-xs text-muted-foreground tabular-nums">{fmtDate(t.updated_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Crea ticket */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="ticket-create-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LifeBuoy className="size-4 text-primary" />Apri un ticket</DialogTitle>
            <DialogDescription>{targetLabel}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Destinatario del ticket *</Label>
              <Select value={assigneeId} onValueChange={setAssigneeId}>
                <SelectTrigger data-testid="ticket-assignee-select">
                  <SelectValue placeholder={assignableUsers.length === 0 ? 'Nessun destinatario disponibile per il tuo ruolo' : 'Seleziona a chi inviare...'} />
                </SelectTrigger>
                <SelectContent>
                  {assignableUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{u.full_name || u.email}</span>
                        <span className="text-[11px] text-muted-foreground">
                          {ROLE_LABELS_LOCAL[u.role] || u.role}
                          {u.company_name ? ` · ${u.company_name}` : ''}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                {admin
                  ? 'Come Super Admin puoi scrivere a chiunque nel sistema (B.O., Area Manager, Agenti, altri Super Admin).'
                  : partner && !isAreaManager?.(user)
                    ? 'Come B.O. puoi scrivere al Super Admin, ai tuoi Area Manager e ai tuoi Agenti.'
                    : agent
                      ? 'Come Agente puoi scrivere al tuo Area Manager (se assegnato) e al B.O. della tua azienda.'
                      : 'Puoi scrivere al tuo B.O. e agli agenti del tuo team.'}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Oggetto *</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Es. Richiesta aggiornamento listino" data-testid="ticket-subject-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="ticket-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Priorità</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bassa">Bassa</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Messaggio *</Label>
              <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Descrivi la tua richiesta..." data-testid="ticket-message-input" />
            </div>
            <AttachmentPicker attachments={formAttachments} setAttachments={setFormAttachments} idPrefix="ticket-create" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="ticket-submit-button">
              {creating ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Send className="size-4 mr-2" />}Invia ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dettaglio ticket */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="ticket-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-8">{selected.subject}</DialogTitle>
                <DialogDescription asChild>
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Badge variant={STATUS_BADGE[selected.status]}>{STATUS_LABELS[selected.status]}</Badge>
                    <Badge variant="secondary">{CATEGORY_LABELS[selected.category]}</Badge>
                    <Badge variant={PRIORITY_BADGE[selected.priority]} className="capitalize">{selected.priority}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {selected.company_name} · aperto da {selected.created_by_name} · {fmtDate(selected.created_at)}
                    </span>
                  </div>
                </DialogDescription>
              </DialogHeader>
              {((admin && selected.target === 'admin') || (partner && selected.target === 'partner')) && (
                <div className="flex items-center gap-2 rounded-lg border bg-secondary/30 px-3 py-2">
                  <UserCheck className="size-4 text-primary shrink-0" />
                  <span className="text-xs text-muted-foreground shrink-0">Assegnato a</span>
                  <Select value={selected.assigned_to || 'none'} onValueChange={handleAssign}>
                    <SelectTrigger className="h-8 flex-1" data-testid="ticket-assign-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non assegnato</SelectItem>
                      {operators.map(o => (
                        <SelectItem key={o.id} value={o.id}>{o.full_name || o.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!((admin && selected.target === 'admin') || (partner && selected.target === 'partner')) && selected.assigned_to_name && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
                  <UserCheck className="size-3.5 text-primary" />In lavorazione da: <span className="font-medium text-foreground">{selected.assigned_to_name}</span>
                </div>
              )}
              <ScrollArea className="flex-1 max-h-[42vh] pr-3" data-testid="ticket-thread">
                <div className="space-y-3">
                  {(selected.messages || []).map(m => {
                    const mine = m.author_id === user?.id;
                    return (
                      <div key={m.id} className={`rounded-lg border p-3 text-sm ${mine ? 'bg-primary/5 border-primary/20 ml-8' : 'bg-secondary/40 mr-8'}`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-xs font-medium">{m.author_name} <span className="text-muted-foreground font-normal">({m.author_role === 'admin' ? 'Scintilla Hub' : m.author_role === 'partner' ? 'Responsabile' : 'Agente'})</span></span>
                          <span className="text-[11px] text-muted-foreground tabular-nums">{fmtDate(m.created_at)}</span>
                        </div>
                        <div className="whitespace-pre-wrap">{m.body}</div>
                        <MessageAttachments attachments={m.attachments} />
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
              <Separator />
              {selected.status !== 'chiuso' ? (
                <div className="space-y-2">
                  <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Scrivi una risposta..." data-testid="ticket-reply-input" />
                  <AttachmentPicker attachments={replyAttachments} setAttachments={setReplyAttachments} idPrefix="ticket-reply" />
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex gap-2">
                      {canHandle && selected.status !== 'in_lavorazione' && (
                        <Button variant="outline" size="sm" onClick={() => changeStatus('in_lavorazione')} data-testid="ticket-take-button">
                          Prendi in carico
                        </Button>
                      )}
                      {canHandle && (
                        <Button variant="outline" size="sm" onClick={() => changeStatus('chiuso')} data-testid="ticket-close-button">
                          <CheckCircle2 className="size-4 mr-1" />Chiudi ticket
                        </Button>
                      )}
                    </div>
                    <Button size="sm" onClick={handleReply} disabled={sendingReply || (!reply.trim() && replyAttachments.length === 0)} data-testid="ticket-reply-button">
                      {sendingReply ? <Loader2 className="size-4 mr-1 animate-spin" /> : <Send className="size-4 mr-1" />}Rispondi
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Questo ticket è chiuso.</span>
                  {canHandle && (
                    <Button variant="outline" size="sm" onClick={() => changeStatus('aperto')} data-testid="ticket-reopen-button">
                      <RotateCcw className="size-4 mr-1" />Riapri
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
